/**
 * assets/warranty-hero-carousel.js
 * Drives the Warranty Hero Carousel: advances the scroll-snap track, keeps the dots in sync
 * and owns the play/pause toggle. One instance per section. The slide count comes from the
 * markup, so adding or removing slides in the theme editor needs no change here.
 *
 * Scrolling itself is native. This file never sets a scroll position by hand except through
 * scrollTo, so swipe, trackpad and keyboard scrolling keep working exactly as the browser
 * intends and the dots follow a drag as readily as a click.
 *
 * The advance is a self-rescheduling timeout rather than an interval, so pressing a dot
 * restarts the full dwell on the slide it lands on instead of inheriting whatever was left
 * of the previous one.
 *
 * THE LOOP SEAM
 * A copy of the first slide is appended to the track, so the rotation can advance forwards off
 * the last slide instead of scrolling all the way back. Slides are the full width of the
 * viewport, so resting on the copy is pixel-identical to resting on the real first slide: once
 * the rotation settles there it jumps back to the top of the track with no animation and
 * nothing visibly changes. That jump is the wrap.
 */
(function () {
  var SECTION_SELECTOR = '.warranty-hero-carousel';
  var PLAYING_CLASS = 'is-playing';
  var LOADED_CLASS = 'is-loaded';
  // How long the track must sit still before we call a scroll finished.
  var IDLE_DELAY = 120;

  /**
   * Fades the first slide's photos up as they load.
   */
  function initImageFade(root) {
    if (!root || root.dataset.whcFadeInit === 'true') return;

    var images = Array.prototype.slice.call(
      root.querySelectorAll('.warranty-hero-carousel__slide:first-child > img')
    );
    if (!images.length) return;

    root.dataset.whcFadeInit = 'true';

    images.forEach(function (img) {
      var reveal = function () {
        img.classList.add(LOADED_CLASS);
      };

      if (img.complete && img.naturalWidth > 0) {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(reveal);
        });
        return;
      }

      // Anything still loading has been painted at opacity 0 for a while by the time these
      // fire, so it can be revealed outright.
      img.addEventListener('load', reveal, { once: true });
      // A photo that fails to load should leave its broken-image box, not a hole the
      // fade never fills.
      img.addEventListener('error', reveal, { once: true });
    });
  }

  function initCarousel(root) {
    if (!root || root.dataset.whcInit === 'true') return;

    var track = root.querySelector('.warranty-hero-carousel__track');
    if (!track) return;

    var slides = Array.prototype.slice.call(
      track.querySelectorAll('.warranty-hero-carousel__slide')
    );
    if (!slides.length) return;

    root.dataset.whcInit = 'true';

    // A single slide is a static hero: no dots, no toggle, nothing to rotate.
    var count = slides.length;
    if (count < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('.warranty-hero-carousel__dot'));
    var controls = root.querySelector('.warranty-hero-carousel__controls');
    var toggle = root.querySelector('.warranty-hero-carousel__toggle');

    if (controls) controls.hidden = false;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var duration = (parseFloat(track.dataset.whcDuration) || 6) * 1000;
    // Reduced motion means no self-advancing content, so the toggle starts on "play"
    // and the hero waits to be driven by hand.
    var autoplayWanted = track.dataset.whcAutoplay === 'true' && !reduceMotion.matches;

    // See THE LOOP SEAM above.
    function makeTailCopy(source) {
      var copy = source.cloneNode(true);

      copy.setAttribute('aria-hidden', 'true');
      copy.removeAttribute('aria-label');
      copy.removeAttribute('aria-roledescription');

      // Editor attributes name a block; a copy is not one, and duplicating them would
      // point two nodes at the same block.
      Array.prototype.slice.call(copy.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-shopify') === 0) copy.removeAttribute(attr.name);
      });

      // The eager, high-priority hints belong to the real first slide alone. A slide is
      // a pair, so both halves need downgrading.
      Array.prototype.forEach.call(copy.querySelectorAll('img'), function (img) {
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      });

      track.appendChild(copy);
      return copy;
    }

    var wrapSlot = count; // where the rotation lands before jumping back to 0
    // Everything the track can come to rest on: the real slides plus the copy.
    var slots = slides.concat([makeTailCopy(slides[0])]);

    var current = 0;
    var timer = null;
    var idleTimer = null;
    var frame = null;

    function slotOffset(i) {
      return slots[i].offsetLeft - track.offsetLeft;
    }

    // The slot whose left edge sits closest to the current scroll position.
    function nearestSlot() {
      var best = 0;
      var bestDistance = Infinity;

      for (var i = 0; i < slots.length; i++) {
        var distance = Math.abs(slotOffset(i) - track.scrollLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }

      return best;
    }

    function setActive(slot) {
      // The wrap slot is the first slide wearing a different hat.
      var active = slot % count;
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }

    function goTo(slot, animate) {
      current = slot;
      track.scrollTo({
        left: slotOffset(slot),
        behavior: animate && !reduceMotion.matches ? 'smooth' : 'auto'
      });
    }

    function clearTimer() {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function schedule() {
      clearTimer();
      timer = window.setTimeout(function () {
        goTo(current + 1, true);
        schedule();
      }, duration);
    }

    function syncToggle() {
      if (!toggle) return;
      var playing = root.classList.contains(PLAYING_CLASS);
      var label = playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay;
      if (label) {
        toggle.setAttribute('aria-label', label);
        toggle.title = label;
      }
    }

    function play() {
      root.classList.add(PLAYING_CLASS);
      syncToggle();
      schedule();
    }

    function pause() {
      root.classList.remove(PLAYING_CLASS);
      syncToggle();
      clearTimer();
    }

    // Called once the track has stopped moving, however it got there.
    function onIdle() {
      var slot = nearestSlot();

      if (slot === wrapSlot) {
        // Resting on the copy of the first slide. Jumping to the real one is invisible.
        goTo(0, false);
        return;
      }

      current = slot;
    }

    track.addEventListener(
      'scroll',
      function () {
        if (!frame) {
          frame = window.requestAnimationFrame(function () {
            frame = null;
            setActive(nearestSlot());
          });
        }

        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(onIdle, IDLE_DELAY);
      },
      { passive: true }
    );

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (root.classList.contains(PLAYING_CLASS)) {
          pause();
        } else {
          play();
        }
      });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i, true);
        // Keep playing if it was, but give this slide its full dwell.
        if (root.classList.contains(PLAYING_CLASS)) schedule();
      });
    });

    // No point burning a timer on a tab nobody is looking at — and returning to
    // the tab should not land mid-slide on one that advanced while hidden.
    document.addEventListener('visibilitychange', function () {
      if (!root.classList.contains(PLAYING_CLASS)) return;
      if (document.hidden) {
        clearTimer();
      } else {
        schedule();
      }
    });

    // Following the setting live means a user turning reduced motion on mid-visit
    // gets the rotation stopped, not just excluded from it at load.
    var onReduceChange = function (event) {
      if (event.matches) pause();
    };
    if (typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', onReduceChange);
    } else if (typeof reduceMotion.addListener === 'function') {
      reduceMotion.addListener(onReduceChange);
    }

    // The theme editor swaps sections out without a page load, so a rotation left
    // running would keep firing against detached nodes for the rest of the session.
    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target.contains(root)) clearTimer();
    });

    goTo(0, false);
    setActive(0);

    if (autoplayWanted) {
      play();
    } else {
      pause();
    }
  }

  function initAll(scope) {
    var containers = (scope || document).querySelectorAll(SECTION_SELECTOR);
    Array.prototype.forEach.call(containers, function (root) {
      initImageFade(root);
      initCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Re-init when the section is re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
